import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArticleCard } from '../components/ArticleCard';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const NewsPage = () => {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const subcategory = searchParams.get('subcategory');
  
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadArticles();
    loadCategories();
  }, [category, subcategory]);

  const loadArticles = async () => {
    try {
      const params = { status: 'published', limit: 50, weighted: !category };
      
      if (category) {
        // Find category ID by slug
        const categoriesRes = await axios.get(`${API}/categories?type=news`);
        const cat = categoriesRes.data.find(c => c.slug === category);
        if (cat) {
          params.category_id = cat.id;
        }
      }
      
      if (subcategory) {
        params.subcategory = subcategory;
      }
      
      const response = await axios.get(`${API}/articles`, { params });
      setArticles(response.data);
    } catch (error) {
      console.error('Failed to load articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, {
        params: { type: 'news' }
      });
      setCategories(response.data);
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="news-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0E2E59] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading news...</p>
        </div>
      </div>
    );
  }

  const currentCategory = category ? categories.find(c => c.slug === category) : null;
  const pageTitle = subcategory 
    ? `${currentCategory?.name || 'News'}: ${subcategory}`
    : currentCategory?.name || 'Latest News';

  return (
    <div className="py-12" data-testid="news-page">
      <div className="container-custom">
        <h1 className="section-header" data-testid="news-title">{pageTitle}</h1>

        {!category && !subcategory && (
          <Tabs defaultValue="all" className="w-full" data-testid="news-tabs">
            <TabsList className="tabs-list">
              <TabsTrigger value="all" className="tab-trigger" data-testid="tab-all-news">ALL</TabsTrigger>
              {categories.map((cat) => (
                <TabsTrigger
                  key={cat.id}
                  value={cat.slug}
                  className="tab-trigger"
                  data-testid={`tab-${cat.slug}`}
                >
                  {cat.name.toUpperCase()}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="all" data-testid="all-news-content">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map((article) => (
                  <ArticleCard key={article.id} article={article} showImage={true} />
                ))}
              </div>
            </TabsContent>

            {categories.map((cat) => (
              <TabsContent key={cat.id} value={cat.slug} data-testid={`content-${cat.slug}`}>
                <CategoryTab categoryId={cat.id} />
              </TabsContent>
            ))}
          </Tabs>
        )}

        {(category || subcategory) && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => (
              <ArticleCard key={article.id} article={article} showImage={true} />
            ))}
            {articles.length === 0 && (
              <div className="col-span-full text-center py-12 text-gray-500">
                No articles found for this category.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const CategoryTab = ({ categoryId }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCategoryArticles();
  }, [categoryId]);

  const loadCategoryArticles = async () => {
    try {
      const response = await axios.get(`${API}/articles`, {
        params: { category_id: categoryId, status: 'published', limit: 50 }
      });
      setArticles(response.data);
    } catch (error) {
      console.error('Failed to load articles:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {articles.map((article) => (
        <ArticleCard key={article.id} article={article} showImage={true} />
      ))}
      {articles.length === 0 && (
        <div className="col-span-full text-center py-8 text-gray-500">
          No articles found in this category.
        </div>
      )}
    </div>
  );
};